import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Skeleton, SpecraBadge, SpecraButton, SpecraHeading, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { SearchX } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FundingBar } from '../../../components/delivery/widgets/FundingBar';
import { KpiSpine } from '../../../components/delivery/widgets/KpiSpine';
import { useProgramFinancialSummary } from '../../../components/delivery/widgets/useProgramFinancialSummary';
import { LifecycleNodeKind, LinkedChips } from '../../../components/LinkedChips';
import { useAuth } from '../../../contexts/authContext';
import { useProgramContext } from '../../../layouts/ProgramLayout';
import { programRef } from '../../../lib/access/types';
import { useView } from '../../../lib/access/useView';
import { useLayoutStore } from '../../../lib/stores/LayoutStore';
import { cn } from '../../../lib/utils';
import { ConfirmDeleteDialog } from '../_shared/components/ConfirmDeleteDialog';
import { EmptyState } from '../_shared/components/PageStates';
import { ProgramKpiStrip } from '../Deliverables/components/KpiStrip';
import { AuditVerdict } from './home/AuditVerdict';
import { DoNextQueue } from './home/DoNextQueue';
import { ProgramBriefingPanel } from './home/ProgramBriefingPanel';
import { OnHoldBanner } from './home/programHomeUi';
import { useDeleteProgram } from './hooks';
import { ProgramEditDialog } from './ProgramEditDialog';
import { PurgeProgramDialog } from './PurgeProgramDialog';
export function ProgramHomePage() {
    const navigate = useNavigate();
    const { programId, program, canEdit, isLoading } = useProgramContext();
    const { user } = useAuth();
    // The Assist rail shrinks the content area; stack the cockpit when it's open.
    const assistOpen = useLayoutStore((s)=>s.assistOpen);
    // Shared (cached) with AuditVerdict/DoNextQueue — drives the state badge.
    const { data: summary } = useProgramFinancialSummary(programId);
    const remove = useDeleteProgram();
    // The info pane shows only what this viewer is allowed to see — no cards
    // that just read "Restricted".
    // Gate each financial widget on ITS OWN fields — a blanket gate on
    // `revenue` hid the funding bar from exec_viewer, whose carve-out exists
    // precisely to show the topline while EAC detail stays masked.
    const homeView = useView(programRef(programId));
    const canSeeFunding = !homeView.fieldMasked('funded_amount') || !homeView.fieldMasked('current_tcv');
    // D104: the deliverables strip and on-time tile count every contract period
    // (cumulative); ?period= narrows both to one (statuses still as of today).
    const [search, setSearch] = useSearchParams();
    const periodId = search.get('period') ?? '';
    const setPeriod = (id)=>setSearch((prev)=>{
            const next = new URLSearchParams(prev);
            if (id) next.set('period', id);
            else next.delete('period');
            return next;
        });
    const [editOpen, setEditOpen] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [purgeOpen, setPurgeOpen] = useState(false);
    const isAdmin = [
        'admin',
        'internal'
    ].includes(user?.role?.toLowerCase() ?? '');
    if (isLoading) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "space-y-3 p-6",
            "data-testid": Testids.deliveryShared.loadingSkeleton,
            children: [
                /*#__PURE__*/ _jsx(Skeleton, {
                    className: "h-8 w-1/3"
                }),
                /*#__PURE__*/ _jsx(Skeleton, {
                    className: "h-32 w-full"
                }),
                /*#__PURE__*/ _jsx(Skeleton, {
                    className: "h-32 w-full"
                })
            ]
        });
    }
    if (!program) {
        return /*#__PURE__*/ _jsx("div", {
            className: "p-6",
            children: /*#__PURE__*/ _jsx(EmptyState, {
                icon: SearchX,
                title: "Program not found",
                description: "It may have been deleted, or you may not have access to it.",
                action: /*#__PURE__*/ _jsx(SpecraButton, {
                    variant: "secondary",
                    size: "sm",
                    onClick: ()=>navigate('/delivery/programs'),
                    children: "Back to programs"
                })
            })
        });
    }
    const handleDelete = async ()=>{
        await remove.mutateAsync(programId);
        setConfirmDelete(false);
        navigate('/delivery/programs');
    };
    const briefing = /*#__PURE__*/ _jsx(ProgramBriefingPanel, {
        program: program,
        programId: programId,
        canEdit: canEdit,
        onOpenFullEdit: ()=>setEditOpen(true)
    });
    const dialogs = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(ProgramEditDialog, {
                open: editOpen,
                onOpenChange: setEditOpen,
                program: program,
                readOnly: !canEdit,
                onDelete: canEdit ? ()=>{
                    setEditOpen(false);
                    setConfirmDelete(true);
                } : undefined,
                onPurge: isAdmin ? ()=>{
                    setEditOpen(false);
                    setPurgeOpen(true);
                } : undefined
            }),
            /*#__PURE__*/ _jsx(ConfirmDeleteDialog, {
                open: confirmDelete,
                onOpenChange: setConfirmDelete,
                title: "Archive program",
                description: `Archive "${program.programNameFull}"? It disappears from Delivery, but nothing is destroyed — its audits, risks, tasks and documents are kept, and an admin can still purge it permanently.`,
                onConfirm: handleDelete,
                isPending: remove.isPending,
                confirmLabel: "Archive",
                pendingLabel: "Archiving…"
            }),
            isAdmin ? /*#__PURE__*/ _jsx(PurgeProgramDialog, {
                open: purgeOpen,
                onOpenChange: setPurgeOpen,
                programId: programId,
                programName: program.programNameFull,
                onPurged: ()=>navigate('/delivery/programs')
            }) : null
        ]
    });
    // The cockpit (LAYOUT-SPEC §2A): a work column (greeting → state summary →
    // audit verdict → KPI strip → Do Next) beside a 324px briefing rail of
    // reference facts. Below lg the rail drops under the work column. The
    // sidebar does the navigating.
    const hour = new Date().getHours();
    const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
    // Greet the person, not the program (mockup: "Good Morning, Shiraz").
    const greetName = user?.name?.trim().split(/\s+/)[0] || 'there';
    const today = new Date().toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric'
    });
    const workColumn = /*#__PURE__*/ _jsx("div", {
        className: "min-w-0 flex-1 px-6 pb-12 pt-8 sm:px-9",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "mx-auto flex max-w-[860px] flex-col gap-[var(--space-5)]",
            children: [
                /*#__PURE__*/ _jsx(OnHoldBanner, {
                    onHoldSince: program.onHoldSince
                }),
                /*#__PURE__*/ _jsxs("header", {
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-baseline gap-3",
                            children: [
                                /*#__PURE__*/ _jsxs(SpecraHeading, {
                                    as: "h1",
                                    size: "home",
                                    children: [
                                        greeting,
                                        ", ",
                                        greetName,
                                        " 👋"
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(SpecraText, {
                                    className: "ml-auto whitespace-nowrap",
                                    size: "caption",
                                    tone: "muted",
                                    children: today
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "mt-2.5 flex items-center gap-2.5",
                            children: [
                                (()=>{
                                    const kpi = summary?.kpiSnapshot;
                                    const hasAudit = !!kpi?.hasAudit && (kpi?.latestAuditTotal ?? 0) > 0;
                                    const ready = hasAudit && (kpi?.latestAuditPass ?? 0) >= (kpi?.latestAuditTotal ?? 0);
                                    return /*#__PURE__*/ _jsx(SpecraBadge, {
                                        variant: ready ? 'success' : 'danger',
                                        children: ready ? 'Audit-ready' : 'Not audit-ready'
                                    });
                                })(),
                                /*#__PURE__*/ _jsx(SpecraText, {
                                    as: "p",
                                    tone: "muted",
                                    children: summary?.kpiSnapshot?.hasAudit && (summary.kpiSnapshot.latestAuditTotal ?? 0) > 0 ? `SDS audit: ${summary.kpiSnapshot.latestAuditPass}/${summary.kpiSnapshot.latestAuditTotal} checks passing.` : 'The SDS audit decides compliance — none is on record yet.'
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(LinkedChips, {
                            className: "mt-2.5",
                            kind: LifecycleNodeKind.PROGRAM,
                            id: programId
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(AuditVerdict, {
                    programId: programId
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "mb-[var(--space-3)] flex flex-col gap-[var(--space-12)]",
                    children: [
                        /*#__PURE__*/ _jsx(KpiSpine, {
                            programId: programId,
                            periodId: periodId
                        }),
                        /*#__PURE__*/ _jsx(ProgramKpiStrip, {
                            programId: programId,
                            periodId: periodId,
                            onPeriodChange: setPeriod
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(DoNextQueue, {
                    programId: programId
                })
            ]
        })
    });
    const rail = // Rounded on the interior (left) side, flush to the right/viewport edge —
    // a left-only radius so the panel reads as the mockup's rounded rail
    // without a wrong notch at the right edge. When the Assist rail steals the
    // horizontal room, the cockpit stacks (below), so the briefing rail spans
    // full width instead of a crushed 324px column.
    /*#__PURE__*/ _jsx("aside", {
        className: cn('shrink-0 rounded-l-[20px] border-border bg-muted/40', !assistOpen && 'lg:w-[324px] lg:border-l'),
        children: /*#__PURE__*/ _jsxs("div", {
            className: "flex flex-col gap-[26px] px-6 pb-12 pt-[26px]",
            children: [
                briefing,
                canSeeFunding && /*#__PURE__*/ _jsx(FundingBar, {
                    programId: programId,
                    variant: "rail",
                    fundedAmount: program?.fundedAmount,
                    totalCeiling: program?.totalCeiling
                })
            ]
        })
    });
    // Tailwind breakpoints track the viewport, not this container — so with the
    // Assist rail open the two-column cockpit would crush (its columns don't know
    // the content area shrank). Stack to a single column whenever Assist is open.
    return /*#__PURE__*/ _jsxs("div", {
        className: cn('flex h-full flex-col overflow-auto bg-card', !assistOpen && 'lg:flex-row lg:overflow-hidden'),
        "data-testid": Testids.deliveryPrograms.detailPage,
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: cn('min-w-0 flex-1', !assistOpen && 'lg:overflow-auto'),
                children: workColumn
            }),
            /*#__PURE__*/ _jsx("div", {
                className: cn(!assistOpen && 'lg:h-full lg:overflow-auto'),
                children: rail
            }),
            dialogs
        ]
    });
}
